import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { missingReadingsApi } from '../../services/admin/missingReadingsApi'
import type { MissingReadingsConfig } from '../../types/missingReadings'

const CONFIG_KEY = ['admin', 'missing-readings', 'config']

const errMessage = (error: any, fallback: string) =>
  error?.response?.data?.error?.message || error?.message || fallback

export const useMissingReadingsConfig = () =>
  useQuery({ queryKey: CONFIG_KEY, queryFn: () => missingReadingsApi.getConfig() })

export const useUpdateMissingReadingsConfig = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Partial<MissingReadingsConfig>) => missingReadingsApi.updateConfig(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONFIG_KEY })
      toast.success('Settings saved')
    },
    onError: (error: any) => toast.error(errMessage(error, 'Failed to save settings')),
  })
}

/** Fleet-wide day-by-day summary for the dashboard card. `enabled` lets the
 * caller gate the query on isAdmin() rather than firing it and eating a 403. */
export const useMissingReadingsSummary = (
  days: number,
  enabled: boolean,
  accountId?: number | null
) =>
  useQuery({
    // accountId is part of the key, so the unscoped call the account column
    // needs and a scoped call for the other columns cache independently —
    // and collapse into one request while nothing is selected.
    queryKey: ['admin', 'missing-readings', 'summary', days, accountId ?? 'all'],
    queryFn: () => missingReadingsApi.getSummary(days, accountId),
    enabled,
    staleTime: 60_000,
  })

/** Per-device breakdown for one selected historical day (YYYY-MM-DD) — the fleet day page. */
export const useMissingReadingsDay = (
  date: string | null,
  enabled: boolean,
  accountId?: number | null
) =>
  useQuery({
    queryKey: ['admin', 'missing-readings', 'day', date, accountId ?? 'all'],
    queryFn: () => missingReadingsApi.getDay(date as string, accountId),
    enabled: enabled && !!date,
    staleTime: 60_000,
  })

/** One device's day-by-day series, for the selected-RIC panel. */
export const useDeviceMissingReadingsSeries = (deviceId: number | null, days: number, enabled: boolean) =>
  useQuery({
    queryKey: ['admin', 'missing-readings', 'device', deviceId, days],
    queryFn: () => missingReadingsApi.getDeviceSeries(deviceId as number, days),
    enabled: enabled && !!deviceId,
    staleTime: 60_000,
  })

/** Sequence-level detail for one device on one day. */
export const useDaySequenceDetail = (deviceId: number | null, date: string | null, enabled: boolean) =>
  useQuery({
    queryKey: ['admin', 'missing-readings', 'sequence', deviceId, date],
    queryFn: () => missingReadingsApi.getDaySequence(deviceId as number, date as string),
    enabled: enabled && !!deviceId && !!date,
    staleTime: 60_000,
  })
